import _ from "lodash";
import React, { useRef, Fragment, useState } from "react";
import classes from "./ButtonComponent.scss";
import { WarningCircleOutlined } from "@toddle-edu/ds-icons";
import { FilterLabelButton } from "@toddle-edu/ds-web";
import { Popover } from "@toddle-edu/react-tiny-popover";
import { containerStyle } from "./ButtonComponentStyles";
import { getLabelValueForMultiFilterLabelButton } from "Utils";
import { getUniqueSelectedValues, findLabelMemoized } from "../utils";
import { ELEMENT_TYPE_MYP_OBJECTIVES } from "Constants/stringConstants";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { Tooltip } from "@toddle-edu/ds-web";

const MoreThanOneOption = ({ parent, label, t }) => {
  return (
    <div className={classes.moreThanOneOptionContainer}>
      <div>
        <WarningCircleOutlined size={"xx-small"} />
      </div>
      <div className={classes.noOptionTitle}>
        {t("snp:select_one_parent_option", {
          parent,
          label,
        })}
      </div>
    </div>
  );
};

const MessageComponent = ({ label }) => {
  return <div className={classes.noOptionMessage}>{label}</div>;
};

const getDisabledStateInfoElement = ({
  label,
  filterDisabilityStatusConfig,
  t,
  getDisabledLabelOnly = false,
}) => {
  const { stateCode, parentFilterLabel } = filterDisabilityStatusConfig;

  switch (stateCode) {
    case "LOADING": {
      if (getDisabledLabelOnly) {
        return t("snp:loading_options");
      }
      return <MessageComponent label={t("snp:loading_options")} />;
    }

    case "NO_OPTIONS": {
      if (getDisabledLabelOnly) {
        return t("common:no_with_label", {
          label: t("common:option_plural"),
        });
      }
      return (
        <MessageComponent
          label={t("common:no_with_label", {
            label: t("common:option_plural"),
          })}
        />
      );
    }

    case "PARENT_FILTER_MULTI_VALUE": {
      if (getDisabledLabelOnly) {
        return t("snp:select_one_parent_option", {
          parent: parentFilterLabel,
          label,
        });
      }
      return (
        <MoreThanOneOption parent={parentFilterLabel} label={label} t={t} />
      );
    }

    default: {
      return null;
    }
  }
};

const ButtonComponent = ({
  label,
  values,
  entityType,
  options,
  filterDisabilityStatusConfig,
  containerParentRef,
  t,
  isCheckList,
  useDsTooltip = false,
}) => {
  const childrenRef = useRef(null);

  const [showPopover, setPopoverVisibility] = useState(false);

  const { isDisabled } = filterDisabilityStatusConfig;

  const handleClick = () => {
    if (isDisabled) {
      setPopoverVisibility(prev => !prev);
    } else {
      setPopoverVisibility(false);
    }
  };

  const getUniqueValues = () => {
    const areAllOptionsSelected = _.size(options) == _.size(values);

    let uniqueValues = [];

    switch (entityType) {
      /**
       * Unique value criteria is not applicable to myp objectives
       * as there are objectives which have same labels but they are always unique
       */
      case ELEMENT_TYPE_MYP_OBJECTIVES: {
        uniqueValues = values;
        break;
      }

      default: {
        /**
         * If all options are selected then no need to filter out
         * duplicate values
         */

        uniqueValues = areAllOptionsSelected
          ? values
          : getUniqueSelectedValues({ values, options });
      }
    }

    return uniqueValues;
  };

  return (
    <Fragment>
      <ConditionalWrapper
        type={"wrapperAsElement"}
        condition={isDisabled && useDsTooltip}
        wrapper={
          <Tooltip
            tooltip={getDisabledStateInfoElement({
              label,
              filterDisabilityStatusConfig,
              t,
              getDisabledLabelOnly: true,
            })}
          />
        }
      >
        <FilterLabelButton
          label={label}
          variant={"plain"}
          ref={childrenRef}
          size="medium"
          onClick={handleClick}
          style={isDisabled ? { cursor: "not-allowed" } : {}}
          labelValue={
            isCheckList
              ? getLabelValueForMultiFilterLabelButton({
                  options,
                  selectedOptions: getUniqueValues() ?? [],
                  t,
                  allText: t("common:all"),
                  noneText: t("common:none"),
                })
              : findLabelMemoized({ options, value: _.first(values), t })
          }
        />
      </ConditionalWrapper>
      {showPopover && !useDsTooltip && (
        <Popover
          isOpen={true}
          positions={["bottom"]}
          reposition={false}
          containerStyle={containerStyle}
          content={getDisabledStateInfoElement({
            label,
            filterDisabilityStatusConfig,
            t,
          })}
          containerParent={containerParentRef.current}
          childrenRef={{ current: childrenRef.current }}
          isChildrenRefPassed={true}
          onClickOutside={() => setPopoverVisibility(false)}
        />
      )}
    </Fragment>
  );
};

export default ButtonComponent;
